'use client';

import type { ProductCardData } from '@/components/brand';
import { AffirmMessage } from '@/components/product/AffirmMessage';
import { CembraSentence } from '@/components/product/CembraSentence';
import { OneySentence } from '@/components/product/OneySentence';
import { StickyPurchaseBar, useCtaOffscreen } from '@/components/product/StickyPurchaseBar';
import { ChangeStrapButton } from '@/components/product/ChangeStrapButton';
import { MarketingSelectorButton } from '@/components/product/MarketingSelectorButton';
import { StrapSelector } from '@/components/product/StrapSelector';
import { V12Sentence } from '@/components/product/V12Sentence';
import { Button, Icon } from '@/components/ui';
import { Breadcrumbs, type BreadcrumbItem } from '@/components/ui/Breadcrumbs';
import { MESSAGE_KEY_MAP } from '@/components/ui/Tax/taxMessageKeys';
import { useAddToCart } from '@/hooks/useAddToCart';
import { useIsCheckoutBusy } from '@/hooks/useCheckout';
import { useContactInfo } from '@/hooks/useContactInfo';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { BRAND_TRAITS } from '@/lib/brands/traits';
import { BRAND } from '@/lib/cms/config';
import { EVENT_NAME } from '@/lib/gtm/const';
import { consumeItemListAttribution } from '@/lib/gtm/itemListAttribution';
import { buildPreOrderItemExtras } from '@/lib/gtm/preOrder';
import { stripLocaleFromPath, useRouter } from '@/lib/i18n/navigation';
import { startNavigationProgress } from '@/components/ui/NavigationProgress';
import { replaceLastPathSegment } from '@/lib/utils/replaceLastPathSegment';
import {
  CTA_LABELS,
  isAddToCartCta,
  isContactUsCta,
  isNotifyMePdpEnabled,
  normalizeCtaType,
  resolveNotifyMeForBrand,
  type CtaType,
} from '@/lib/product/cta';
import type { Product, ServicePerk } from '@/lib/product/types';
import { getTotalInfoMessage } from '@/lib/tax';
import { modalActions } from '@/states/modalState';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { type ReactNode, type Ref, useEffect } from 'react';
import styles from './ProductInfo.module.scss';

// Mapping from perk type/title to translation keys
const perkTitleKeys: Record<string, string> = {
  freeShipping: 'servicePerks.freeShipping',
  'Free Shipping': 'servicePerks.freeShipping',
  freeReturn: 'servicePerks.freeReturn',
  'Free Return': 'servicePerks.freeReturn',
  giftService: 'servicePerks.giftingService',
  'Gifting Service': 'servicePerks.giftingService',
  engraving: 'servicePerks.engraving',
  Engraving: 'servicePerks.engraving',
};

const CTA_TRANSLATION_KEYS: Record<string, string> = {
  ORDER: 'addToBag',
  PRE_ORDER: 'preOrder',
  NOTIFY: 'notifyMe',
  DISCOVER: 'discover',
  BOOK_APPOINTMENT: 'bookAppointment',
  ENGRAVING: 'addEngraving',
  CONTACT_US: 'contactUs',
  NO_CTA: 'contactUs',
};

/** Deduplicate first word if repeated (e.g. "Navitimer Navitimer Automatic"). */
function buildDisplayName(name: string): string {
  const words = name.split(' ');
  if (words.length >= 2 && words[0].toLowerCase() === words[1].toLowerCase()) {
    return words.slice(1).join(' ');
  }
  return name;
}

function isEngravingServicePerk(perk: ServicePerk): boolean {
  return (
    perk.icon === 'engraving' ||
    perk.title?.toLowerCase() === 'engraving' ||
    perk.id?.toLowerCase().includes('engraving')
  );
}

function filterVisibleServicePerks(
  servicePerks: ServicePerk[],
  engravingEnabled: boolean,
  ctaType: CtaType | null
): ServicePerk[] {
  return servicePerks.filter(
    (perk) => !isEngravingServicePerk(perk) || (engravingEnabled && ctaType === 'ORDER')
  );
}

function translateServicePerkTitle(perk: ServicePerk, tProduct: (key: string) => string): string {
  const key = perkTitleKeys[perk.type || ''] || perkTitleKeys[perk.title];
  if (key) {
    const translated = tProduct(key);
    if (translated && translated !== key) {
      return translated;
    }
  }
  return perk.title;
}

/** Strip " | ID" suffix from attribute values (e.g. "18k red gold | 18KREG"). */
function cleanAttributeValue(val: string | undefined): string | undefined {
  if (!val) return undefined;
  const pipeIndex = val.indexOf('|');
  return pipeIndex !== -1 ? val.substring(0, pipeIndex).trim() : val.trim();
}

function isGiftServicePerk(perk: ServicePerk): boolean {
  return perk.type?.toLowerCase() === 'giftservice' || perk.title?.toLowerCase().includes('gift');
}

function buildGalletPerkRows(params: {
  visiblePerks: ServicePerk[];
  boutiquePerkRow: ReactNode;
  translateTitle: (perk: ServicePerk) => string;
}): ReactNode[] {
  const { visiblePerks, boutiquePerkRow, translateTitle } = params;
  const rows: ReactNode[] = [];
  let boutiqueInserted = false;

  for (const perk of visiblePerks) {
    if (isGiftServicePerk(perk) && boutiquePerkRow && !boutiqueInserted) {
      rows.push(boutiquePerkRow);
      boutiqueInserted = true;
    }
    rows.push(
      <li key={perk.id} className={styles.galletPerk}>
        <GalletPerkCheckIcon />
        <span className={styles.galletPerkTitle}>{translateTitle(perk)}</span>
      </li>
    );
  }

  if (boutiquePerkRow && !boutiqueInserted) {
    rows.push(boutiquePerkRow);
  }

  return rows;
}

type PrimaryCtaActionParams = {
  ctaType: CtaType;
  canAddToCart: boolean;
  onAddToBag?: () => void;
  onContactUs: () => void;
  onNotifyMe: () => void;
  addToCart: (product: ProductCardData) => Promise<unknown>;
  getProductCard: () => ProductCardData;
};

async function runPrimaryCtaAction({
  ctaType,
  canAddToCart,
  onAddToBag,
  onContactUs,
  onNotifyMe,
  addToCart,
  getProductCard,
}: PrimaryCtaActionParams): Promise<void> {
  if (ctaType === 'NOTIFY') {
    if (!isNotifyMePdpEnabled()) {
      return;
    }

    onNotifyMe();
    return;
  }

  if (isContactUsCta(ctaType)) {
    onContactUs();
    return;
  }

  if (onAddToBag) {
    onAddToBag();
    return;
  }

  if (canAddToCart) {
    await addToCart(getProductCard());
  }
}

/** Phone handset icon after Contact Us CTA label (Figma / BEC-24578). */
const contactUsIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={styles.ctaIcon}
  >
    <path
      d="M4.86577 2.50633H4.88536C5.0393 2.50913 5.19045 2.55811 5.34859 2.65747C5.53613 2.77363 5.72926 2.97796 5.92799 3.26626L7.61998 5.69299C7.75154 5.88053 7.75993 6.01348 7.75154 6.10864C7.73474 6.31297 7.68016 6.48371 7.5906 6.62786L6.82647 7.82583C6.52138 8.29746 6.49199 8.82788 6.74809 9.28271L6.75369 9.29391C6.92023 9.57941 7.15535 9.90829 7.47304 10.2988C7.77673 10.6724 8.10421 11.0349 8.44709 11.3778C8.67521 11.6059 8.92712 11.841 9.19582 12.0761C9.46173 12.3098 9.71784 12.5226 9.95575 12.7101C10.2077 12.9088 10.4036 13.0516 10.5533 13.1467L10.5645 13.1537L10.5771 13.1607C10.8262 13.3077 11.0838 13.3861 11.3413 13.3931C11.3539 13.3931 11.3665 13.3931 11.379 13.3931C11.603 13.3931 11.8339 13.3427 12.0648 13.2419L12.083 13.2349L12.0998 13.2265L13.4195 12.5785C13.7498 12.4176 13.9555 12.384 14.0689 12.384C14.0773 12.384 14.0857 12.384 14.0927 12.384C14.2424 12.391 14.416 12.461 14.6077 12.5925L16.7489 14.0592C17.0302 14.2523 17.2318 14.4469 17.3479 14.6372C17.4459 14.7968 17.4935 14.9479 17.4935 15.099C17.4935 15.2418 17.4557 15.3985 17.3815 15.5651C17.2961 15.7582 17.1534 15.9667 16.9575 16.1837L16.9435 16.1977C16.9155 16.2298 16.8889 16.2606 16.8637 16.2872C16.8287 16.3264 16.7909 16.367 16.7531 16.4118C16.4103 16.7938 16.0338 17.0709 15.6363 17.2333C15.2193 17.404 14.6875 17.4908 14.0563 17.4908C13.3439 17.4908 12.5714 17.341 11.7569 17.0458C10.9144 16.7407 10.0397 16.2858 9.15524 15.6952C8.26096 15.0976 7.37227 14.3559 6.50738 13.4854C5.63549 12.6191 4.88956 11.7262 4.29197 10.832C3.70138 9.94888 3.24934 9.07279 2.94705 8.22889C2.65455 7.41158 2.5062 6.63486 2.5062 5.92251C2.5062 5.50126 2.54539 5.122 2.62376 4.79451C2.69654 4.49082 2.8099 4.21092 2.96244 3.96321C3.11639 3.7127 3.32631 3.46918 3.58522 3.24246L3.60621 3.22427L3.62581 3.20468C3.644 3.18648 3.66219 3.16969 3.68179 3.1543C3.72937 3.11371 3.77555 3.07173 3.82034 3.02834C4.215 2.67987 4.56767 2.50353 4.86716 2.50353M4.86577 1.66663C4.34655 1.66663 3.80634 1.91714 3.24514 2.41816C3.21155 2.45175 3.17517 2.48533 3.13878 2.51612C3.10239 2.54691 3.06601 2.5805 3.03242 2.61409C2.70773 2.89819 2.44603 3.20328 2.2473 3.52656C2.04857 3.85125 1.90162 4.20952 1.80785 4.60138C1.71409 4.99324 1.6665 5.43548 1.6665 5.92391C1.6665 6.73702 1.83025 7.60051 2.15773 8.51439C2.48521 9.42686 2.96384 10.3561 3.59502 11.3008C4.22619 12.2455 5.00012 13.1719 5.91679 14.083C6.82087 14.9927 7.74594 15.7638 8.6906 16.395C9.63527 17.0262 10.5617 17.5076 11.4728 17.8365C12.3825 18.1668 13.2446 18.3319 14.0591 18.3319C14.805 18.3319 15.4376 18.2255 15.9582 18.0114C16.4788 17.7987 16.9547 17.4502 17.3871 16.966C17.4207 16.9268 17.4543 16.889 17.4851 16.8554C17.5159 16.8218 17.5495 16.784 17.583 16.7449C17.8391 16.4608 18.0281 16.1809 18.1498 15.9038C18.2716 15.6281 18.3332 15.3594 18.3332 15.0976C18.3332 14.7898 18.2436 14.4903 18.0645 14.1978C17.8853 13.9053 17.6054 13.6268 17.2234 13.3651L15.0807 11.897C14.756 11.6745 14.4398 11.5569 14.1291 11.5429C14.1081 11.5429 14.0871 11.5415 14.0661 11.5415C13.7708 11.5415 13.4321 11.6339 13.0487 11.82L11.7261 12.468C11.6016 12.5226 11.484 12.5492 11.3748 12.5492C11.3692 12.5492 11.3637 12.5492 11.3581 12.5492C11.2447 12.5464 11.1243 12.5086 10.9998 12.4344C10.8752 12.3546 10.6989 12.2259 10.4708 12.0467C10.2427 11.8676 10.0019 11.6661 9.74583 11.4407C9.48972 11.2154 9.2532 10.9957 9.03768 10.7788C8.71299 10.4541 8.4079 10.1154 8.12101 9.76274C7.83411 9.41007 7.61859 9.11198 7.47724 8.86706C7.37507 8.68513 7.39187 8.4892 7.52902 8.27787L8.29734 7.0743C8.46248 6.81259 8.55905 6.5131 8.58704 6.17442C8.61503 5.83574 8.52126 5.51246 8.30574 5.20597L6.61654 2.78203C6.35483 2.40136 6.08053 2.12006 5.79363 1.94093C5.50534 1.76459 5.20864 1.67222 4.90075 1.66663C4.88956 1.66663 4.87836 1.66663 4.86577 1.66663Z"
      fill="currentColor"
    />
  </svg>
);

export interface ProductInfoProps {
  product: Product;
  servicePerks?: ServicePerk[];
  /** Breadcrumb items to display above product name */
  breadcrumbs?: BreadcrumbItem[];
  /** Whether boutique pickup is available (respects BOUTIQUE_PICKUP module, catalog mode, and CTA type) */
  boutiquePickupAvailable?: boolean;
  /** Whether "Call to Buy" link should be shown (respects phoneCTA, stock, and catalog mode) */
  showCallToBuy?: boolean;
  /** Whether engraving is enabled for this product */
  engravingEnabled?: boolean;
  /** Whether 4Gift is available for this product (country + product level check) */
  giftAvailable?: boolean;
  /** Whether Affirm financing is available for this country */
  affirmEnabled?: boolean;
  /** Maximum Affirm payment amount (from config) */
  affirmPaymentMax?: number;
  /** Whether PDP Cembra financing module is active for this market */
  cembraEnabled?: boolean;
  /** Maximum Cembra financing amount (from config, CH) */
  cembraFinancingPaymentMax?: number;
  /** ISO country code used to pick the Affirm market config (defaults to US) */
  countryCode?: string;
  /** Whether PDP V12 financing module is active for this market */
  v12Enabled?: boolean;
  /** V12 payment min amount from market config (GB) */
  v12PaymentMin?: number;
  /** V12 payment max amount from market config (GB) */
  v12PaymentMax?: number;
  /** Whether PDP Oney 3x/4x financing module is active for this market */
  oneyEnabled?: boolean;
  /** Oney 3x payment min amount from market config (FR) */
  oney3xMin?: number;
  /** Oney 3x payment max amount from market config (FR) */
  oney3xMax?: number;
  /** Oney 4x payment min amount from market config (FR) */
  oney4xMin?: number;
  /** Oney 4x payment max amount from market config (FR) */
  oney4xMax?: number;
  /** When true, catalog browsing market — hide commerce CTAs (Gallet: availability request) */
  isCatalogMode?: boolean;
  /** Catalog PDP availability-request CTA (module + brand capability; BEC-25290) */
  showAvailabilityRequest?: boolean;
  /** When true, show secondary Notify Me link (from pre_order_config_v2) */
  showNotifyMe?: boolean;
  /** Variant control rendered between the price and the CTAs (strap size selector) */
  sizeSelector?: ReactNode;
  onAddToBag?: () => void;
  onGiveAsGift?: () => void;
  className?: string;
}

// Pre-order message now comes from translations: product.preOrderMessage

/**
 * Product information panel with price, CTAs, and service perks
 * Positioned alongside the gallery on desktop
 */
export function ProductInfo({
  product,
  servicePerks = [],
  breadcrumbs,
  boutiquePickupAvailable = false,
  showCallToBuy = false,
  engravingEnabled = false,
  giftAvailable = false,
  affirmEnabled = false,
  affirmPaymentMax,
  cembraEnabled = false,
  cembraFinancingPaymentMax,
  countryCode,
  v12Enabled = false,
  v12PaymentMin,
  v12PaymentMax,
  oneyEnabled = false,
  oney3xMin,
  oney3xMax,
  oney4xMin,
  oney4xMax,
  isCatalogMode = false,
  showAvailabilityRequest = false,
  showNotifyMe = false,
  sizeSelector,
  onAddToBag,
  onGiveAsGift,
  className,
}: Readonly<ProductInfoProps>) {
  const router = useRouter();
  const { sendEcommerceEventBySkus, sendIntentionEvent, sendCallToBuyEvent } = useGoogleAnalytics();
  const t = useTranslations('product.cta');
  const tProduct = useTranslations('product');
  const tPrice = useTranslations('price');

  useEffect(() => {
    if (!product.sku) return;

    const ctaType = normalizeCtaType(product.ctaType);
    const itemListName = consumeItemListAttribution(product.sku);
    const eventExtras =
      ctaType === 'PRE_ORDER'
        ? buildPreOrderItemExtras({
            ctaType: 'PRE_ORDER',
            productType: product.productType,
            ...(itemListName ? { itemListName } : {}),
          })
        : undefined;

    void sendEcommerceEventBySkus({
      event: EVENT_NAME.VIEW_ITEM,
      skus: [product.sku],
      itemListName,
      eventExtras,
      ...(ctaType === 'PRE_ORDER' ? { preOrderSkus: [product.sku] } : {}),
    });
  }, [product.sku, product.ctaType, product.productType, sendEcommerceEventBySkus]);

  const tCommon = useTranslations('common');
  const {
    id,
    name,
    sku,
    price,
    images,
    bundleVariations,
    marketingPartnerSelector,
    ctaType: rawCtaType,
    caseMaterial,
    dialColor,
    caseBackImage,
    backEngravingCoordinates,
    warrantyExtensionDuration,
  } = product;

  const { addToCart, addingProductId } = useAddToCart({ itemListName: 'PDP' });
  const isCheckoutBusy = useIsCheckoutBusy();

  const displayName = buildDisplayName(name);
  const translatePerkTitle = (perk: ServicePerk) => translateServicePerkTitle(perk, tProduct);

  // Normalize CTA type — null from resolveEffectivePdpCta means hide primary button
  const showPrimaryCta = rawCtaType != null && rawCtaType !== '';
  const ctaType = showPrimaryCta ? resolveNotifyMeForBrand(normalizeCtaType(rawCtaType)) : null;
  const notifyMePdpEnabled = isNotifyMePdpEnabled();
  const showNotifyMePrimary = ctaType !== 'NOTIFY' || notifyMePdpEnabled;
  const isPreOrder = ctaType === 'PRE_ORDER';
  const canAddToCart = isAddToCartCta(ctaType ?? undefined);
  const ctaKey = (ctaType && CTA_TRANSLATION_KEYS[ctaType]) || 'addToBag';
  const primaryCtaLabel = ctaType
    ? t(ctaKey, { defaultValue: CTA_LABELS[ctaType] || 'Add to Bag' })
    : '';

  const handleContactUs = () => {
    modalActions.open('contact', {
      source: 'pdp',
      product: {
        sku,
        name: displayName,
        image: images?.[0]?.url,
        material: caseMaterial,
        // BEC-24936: WW/catalog-only markets have no sellable price
        ...(isCatalogMode
          ? {}
          : {
              price: price.amount,
              currency: price.currency,
              formattedPrice: price.formatted,
            }),
      },
    });
  };

  const handleNotifyMe = () => {
    modalActions.open('notify-me', {
      sku,
      productName: displayName,
      productImage: images?.[0]?.url,
      caseMaterial,
      dialColor,
      ...(isCatalogMode ? {} : { price: price.amount, currency: price.currency }),
    });
  };

  const buildOptimisticProductCard = (): ProductCardData => {
    const cleanedMaterial = cleanAttributeValue(caseMaterial);
    const cleanedColor = cleanAttributeValue(dialColor);
    const variantParts = [cleanedMaterial, cleanedColor].filter(Boolean);
    const variantName = variantParts.length > 0 ? variantParts.join(' - ') : undefined;

    return {
      id: id || sku,
      variantId: id,
      sku,
      name,
      subtitle: variantName,
      material: cleanedMaterial,
      dialColor: cleanedColor,
      price: price.amount,
      priceAmount: price.amount,
      currency: price.currency,
      image: images?.[0]?.url || '',
      href: '',
      ctaType: ctaType ?? undefined,
      warrantyExtensionDuration,
    };
  };

  const handlePrimaryCta = async () => {
    if (!ctaType) return;
    await runPrimaryCtaAction({
      ctaType,
      canAddToCart,
      onAddToBag,
      onContactUs: handleContactUs,
      onNotifyMe: handleNotifyMe,
      addToCart,
      getProductCard: buildOptimisticProductCard,
    });
  };

  const handlePriceInfoClick = () => {
    modalActions.open('price');
  };

  const handleBoutiqueInfoClick = () => {
    modalActions.open('boutiquePickup');
  };

  // Get contact info for phone number
  const { phoneHref } = useContactInfo();
  const handleCallToBuy = () => {
    void sendCallToBuyEvent(sku);
    window.location.href = phoneHref;
  };

  const handleBookAppointment = () => {
    sendIntentionEvent(EVENT_NAME.INTENTION_BOOK_APPT);
    modalActions.open('book-appointment', {
      sku,
      productName: displayName,
    });
  };

  const handleAvailabilityRequest = () => {
    sendIntentionEvent(EVENT_NAME.INTENTION_AVAILABILITY_REQUEST);
    const includePrice = price.amount > 0;
    modalActions.open('availability-request', {
      productSku: sku,
      productName: displayName,
      productImage: images?.[0]?.url,
      material: caseMaterial,
      dialColor,
      ...(includePrice
        ? {
            formattedPrice: price.formatted,
            price: price.amount,
            currency: price.currency,
          }
        : {}),
      productPath:
        globalThis.window !== undefined ? globalThis.window.location.pathname : undefined,
    });
  };

  const handleEngraving = () => {
    modalActions.open('engraving', {
      variantId: id,
      sku,
      productName: displayName,
      // Pass case back image for engraving preview (not the front soldier image)
      caseBackImage,
      backEngravingCoordinates,
      // Fallback to first image if no case back
      productImage: caseBackImage || images?.[0]?.url,
    });
  };

  const isAdding = addingProductId === (id || sku);
  const showPrice = price.amount > 0;

  // Country-driven tax label: same policy Checkout renders (VAT Incl. for VAT
  // markets, Excl. Sales Tax for US/CA, nothing for no-tax countries), keyed off
  // the market country in the locale rather than the language file.
  const taxCountryCode = useLocale().split('-')[0].toUpperCase();
  const taxMessageKey = getTotalInfoMessage(taxCountryCode);
  const taxTranslationKey = taxMessageKey ? MESSAGE_KEY_MAP[taxMessageKey] : null;
  const isGallet = BRAND === 'gallet';
  const hasOnPagePrimaryCta =
    isCatalogMode || Boolean(showPrimaryCta && showNotifyMePrimary && ctaType);
  const isStickyPurchaseBarEnabled = BRAND_TRAITS.pdp.stickyPurchaseBar && hasOnPagePrimaryCta;
  const {
    ref: primaryCtaRef,
    element: primaryCtaElement,
    isOffscreen: isPrimaryCtaOffscreen,
  } = useCtaOffscreen<HTMLButtonElement>(isStickyPurchaseBarEnabled);

  // Gallet perks checklist: drop engraving (unless enabled) and inject the
  // Boutique delivery row before Gifting service so the DOM order matches Figma
  // (Free shipping, Free return, Boutique delivery available, Gifting service).
  const galletVisiblePerks = filterVisibleServicePerks(servicePerks, engravingEnabled, ctaType);

  const boutiquePerkRow = boutiquePickupAvailable ? (
    <li key="boutique-delivery" className={styles.galletPerk}>
      <GalletPerkCheckIcon />
      {/* Gallet perk row copy is "Boutique delivery available" (own key); the
          shared boutiquePickupAvailable key stays "pick-up" for Breitling. The
          info icon still opens the boutiquePickup modal — its pick-up body copy
          is a flagged content contradiction to resolve on the ticket. */}
      <span className={styles.galletPerkTitle}>{tProduct('boutiqueDeliveryAvailable')}</span>
      <button
        type="button"
        className={styles.infoIcon}
        aria-label={tProduct('ariaLabels.boutiqueInfo')}
        onClick={handleBoutiqueInfoClick}
      >
        <InfoBadgeIcon />
      </button>
    </li>
  ) : null;

  const galletPerkRows = buildGalletPerkRows({
    visiblePerks: galletVisiblePerks,
    boutiquePerkRow,
    translateTitle: translatePerkTitle,
  });

  return (
    <div className={clsx(styles.info, className)}>
      {/* Breadcrumbs - above product name (Breitling only; Gallet relies on header breadcrumbs) */}
      {!isGallet && breadcrumbs && breadcrumbs.length > 0 && (
        <div className={styles.breadcrumbsWrapper}>
          <Breadcrumbs items={breadcrumbs} className={styles.breadcrumbs} />
        </div>
      )}

      {/* Header — Gallet renders name then SKU; Breitling keeps SKU above name */}
      <div className={styles.header}>
        {isGallet ? (
          <>
            <h1 className={styles.name}>{displayName}</h1>
            <span className={styles.sku}>{sku}</span>
          </>
        ) : (
          <>
            <span className={styles.sku}>{sku}</span>
            <h1 className={styles.name}>{displayName}</h1>
          </>
        )}
      </div>

      {showPrice && (
        <div className={styles.pricing}>
          <div className={styles.priceRow}>
            <span className={styles.price}>{price.formatted}</span>
            <button
              type="button"
              className={styles.infoIcon}
              aria-label={tProduct('ariaLabels.priceInfo')}
              onClick={handlePriceInfoClick}
            >
              {isGallet ? (
                <InfoBadgeIcon />
              ) : (
                <Image
                  src="/images/breitling/info-badge.svg"
                  alt=""
                  width={16}
                  height={16}
                  aria-hidden
                />
              )}
            </button>
            {taxTranslationKey && (
              <span className={styles.taxNote}>{tPrice(taxTranslationKey)}</span>
            )}
          </div>
        </div>
      )}

      {showPrice && (
        <ProductInfoFinancing
          isCatalogMode={isCatalogMode}
          affirmEnabled={affirmEnabled}
          ctaType={ctaType}
          price={price}
          affirmPaymentMax={affirmPaymentMax}
          countryCode={countryCode}
          cembraEnabled={cembraEnabled}
          cembraFinancingPaymentMax={cembraFinancingPaymentMax}
          v12Enabled={v12Enabled}
          v12PaymentMin={v12PaymentMin}
          v12PaymentMax={v12PaymentMax}
          oneyEnabled={oneyEnabled}
          oney3xMin={oney3xMin}
          oney3xMax={oney3xMax}
          oney4xMin={oney4xMin}
          oney4xMax={oney4xMax}
          primaryCtaLabel={primaryCtaLabel}
          onPrimaryCta={handlePrimaryCta}
        />
      )}

      <ProductInfoVariantSelectors
        isGallet={isGallet}
        sku={sku}
        images={images}
        bundleVariations={bundleVariations}
        marketingPartnerSelector={marketingPartnerSelector}
        router={router}
      />

      {/* Boutique availability — Breitling keeps it above the CTAs; Gallet
          renders it as a perk row inside the checklist (Figma order). */}
      {!isGallet && boutiquePickupAvailable && (
        <div className={styles.boutiquePickup}>
          <span className={styles.boutiqueText}>{tProduct('boutiquePickupAvailable')}</span>
          <button
            type="button"
            className={styles.infoIcon}
            aria-label={tProduct('ariaLabels.boutiqueInfo')}
            onClick={handleBoutiqueInfoClick}
          >
            <Image
              src="/images/breitling/info-badge.svg"
              alt=""
              width={16}
              height={16}
              aria-hidden
            />
          </button>
        </div>
      )}

      {sizeSelector}

      <ProductInfoActions
        isCatalogMode={isCatalogMode}
        showAvailabilityRequest={showAvailabilityRequest}
        isGallet={isGallet}
        engravingEnabled={engravingEnabled}
        ctaType={ctaType}
        showPrimaryCta={showPrimaryCta && showNotifyMePrimary}
        primaryCtaLabel={primaryCtaLabel}
        canAddToCart={canAddToCart}
        isAdding={isAdding}
        isCheckoutBusy={isCheckoutBusy}
        primaryCtaRef={primaryCtaRef}
        showCallToBuy={showCallToBuy}
        isPreOrder={isPreOrder}
        giftAvailable={giftAvailable}
        sku={sku}
        displayName={displayName}
        preOrderMessage={tProduct('preOrderMessage')}
        orLabel={tCommon('or')}
        labels={{
          addEngraving: t('addEngraving'),
          callToBuy: t('callToBuy'),
          giveAsGift: t('giveAsGift'),
          gift: t('gift'),
          contactUs: t('contactUs'),
          availabilityRequest: t('availabilityRequest'),
        }}
        onEngraving={handleEngraving}
        onPrimaryCta={handlePrimaryCta}
        onCallToBuy={handleCallToBuy}
        onContactUs={handleContactUs}
        onAvailabilityRequest={handleAvailabilityRequest}
        onGiveAsGift={onGiveAsGift}
      />

      {/* Gallet: Book an Appointment sits directly below the CTAs and above the
          perks checklist (Figma order — position 7). */}
      {isGallet && (
        <div className={styles.galletSecondaryActions}>
          <BookAppointmentButton
            className={styles.galletBookAppointment}
            label={t('bookAppointment')}
            onClick={handleBookAppointment}
          />
          {/* Notify Me sits with Book an Appointment, directly under the CTAs */}
          {showNotifyMe && notifyMePdpEnabled && !isCatalogMode && (
            <NotifyMeButton
              className={styles.galletBookAppointment}
              label={t('notifyMe')}
              onClick={handleNotifyMe}
            />
          )}
        </div>
      )}

      {/* Gallet: service perks checklist directly under Book an Appointment.
          Boutique delivery is injected as a perk row before Gifting service
          (Figma order: Free shipping, Free return, Boutique delivery, Gifting). */}
      {isGallet && (galletVisiblePerks.length > 0 || boutiquePickupAvailable) && (
        <ul className={styles.galletPerks}>{galletPerkRows}</ul>
      )}

      <ProductInfoSecondaryLinks
        isGallet={isGallet}
        isCatalogMode={isCatalogMode}
        showCallToBuy={showCallToBuy}
        showNotifyMe={showNotifyMe && notifyMePdpEnabled}
        callToBuyLabel={t('callToBuy')}
        bookAppointmentLabel={t('bookAppointment')}
        notifyMeLabel={t('notifyMe')}
        onCallToBuy={handleCallToBuy}
        onBookAppointment={handleBookAppointment}
        onNotifyMe={handleNotifyMe}
      />

      <ProductInfoBreitlingPerks
        isGallet={isGallet}
        servicePerks={servicePerks}
        engravingEnabled={engravingEnabled}
        ctaType={ctaType}
        translatePerkTitle={translatePerkTitle}
      />

      {isStickyPurchaseBarEnabled && (
        <StickyPurchaseBar
          visible={isPrimaryCtaOffscreen}
          returnFocusTo={primaryCtaElement}
          name={displayName}
          imageUrl={(images?.[1] ?? images?.[0])?.url}
          price={showPrice ? price.formatted : undefined}
        >
          {isCatalogMode && showAvailabilityRequest ? (
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={handleAvailabilityRequest}
              className={styles.callToBuyCta}
            >
              {t('availabilityRequest')}
            </Button>
          ) : (
            <PrimaryPurchaseButton
              isCatalogMode={isCatalogMode}
              isGallet={isGallet}
              primaryCtaLabel={primaryCtaLabel}
              contactUsLabel={t('contactUs')}
              canAddToCart={canAddToCart}
              isAdding={isAdding}
              isCheckoutBusy={isCheckoutBusy}
              onPrimaryCta={handlePrimaryCta}
              onContactUs={handleContactUs}
            />
          )}
        </StickyPurchaseBar>
      )}
    </div>
  );
}

/**
 * Get the icon path for a service perk (Breitling only - uses image files)
 */
function getPerkIconPath(icon: string): string {
  const iconMap: Record<string, string> = {
    truck: '/images/breitling/icons/shipping.svg',
    return: '/images/breitling/icons/return.svg',
    gift: '/images/breitling/icons/give-gift.svg',
    engraving: '/images/breitling/icons/engraving.svg',
  };
  return iconMap[icon] || '/images/breitling/icons/shipping.svg';
}

/**
 * Inline SVG perk icon for Gallet (uses stroke="currentColor" for CSS coloring)
 */
function GalletPerkIcon({ icon }: { icon: string }) {
  switch (icon) {
    case 'truck':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
          />
        </svg>
      );
    case 'return':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
          />
        </svg>
      );
    case 'gift':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z"
          />
        </svg>
      );
    default:
      return null;
  }
}

function navigateToSkuPath(
  router: { push: (href: string) => void },
  sku: string,
  options?: { stripLocale?: boolean }
): void {
  if (options?.stripLocale) {
    const pathWithoutLocale = stripLocaleFromPath(globalThis.location.pathname);
    startNavigationProgress();
    router.push(replaceLastPathSegment(pathWithoutLocale, sku));
    return;
  }

  const pathParts = globalThis.location.pathname.split('/');
  pathParts[pathParts.length - 1] = sku;
  startNavigationProgress();
  router.push(pathParts.join('/'));
}

function ProductInfoFinancing({
  isCatalogMode,
  affirmEnabled,
  ctaType,
  price,
  affirmPaymentMax,
  countryCode,
  cembraEnabled,
  cembraFinancingPaymentMax,
  v12Enabled,
  v12PaymentMin,
  v12PaymentMax,
  oneyEnabled,
  oney3xMin,
  oney3xMax,
  oney4xMin,
  oney4xMax,
  primaryCtaLabel,
  onPrimaryCta,
}: Readonly<{
  isCatalogMode: boolean;
  affirmEnabled: boolean;
  ctaType: CtaType | null;
  price: Product['price'];
  affirmPaymentMax?: number;
  countryCode?: string;
  cembraEnabled: boolean;
  cembraFinancingPaymentMax?: number;
  v12Enabled: boolean;
  v12PaymentMin?: number;
  v12PaymentMax?: number;
  oneyEnabled: boolean;
  oney3xMin?: number;
  oney3xMax?: number;
  oney4xMin?: number;
  oney4xMax?: number;
  primaryCtaLabel: string;
  onPrimaryCta: () => void;
}>) {
  if (isCatalogMode || !ctaType) return null;

  return (
    <>
      {affirmEnabled ? (
        <AffirmMessage
          className={styles.affirmSentence}
          ctaType={ctaType}
          priceAmount={price.amount}
          affirmPaymentMax={affirmPaymentMax}
          countryCode={countryCode}
        />
      ) : null}
      <CembraSentence
        className={styles.cembraSentence}
        amount={price.amount}
        currency={price.currency}
        countryCode={countryCode ?? ''}
        ctaType={ctaType}
        cembraEnabled={cembraEnabled}
        cembraFinancingPaymentMax={cembraFinancingPaymentMax}
        isCatalogMode={isCatalogMode}
      />
      {v12Enabled && countryCode ? (
        <V12Sentence
          className={styles.v12Sentence}
          amount={price.amount}
          currency={price.currency}
          countryCode={countryCode}
          ctaType={ctaType}
          v12PaymentMin={v12PaymentMin}
          v12PaymentMax={v12PaymentMax}
          ctaLabel={primaryCtaLabel}
          onAddToCart={onPrimaryCta}
        />
      ) : null}
      {oneyEnabled ? (
        <OneySentence
          className={styles.oneySentence}
          amount={price.amount}
          currency={price.currency}
          ctaType={ctaType}
          oneyEnabled={oneyEnabled}
          oney3xMin={oney3xMin}
          oney3xMax={oney3xMax}
          oney4xMin={oney4xMin}
          oney4xMax={oney4xMax}
        />
      ) : null}
    </>
  );
}

function ProductInfoBreitlingPerks({
  isGallet,
  servicePerks,
  engravingEnabled,
  ctaType,
  translatePerkTitle,
}: Readonly<{
  isGallet: boolean;
  servicePerks: ServicePerk[];
  engravingEnabled: boolean;
  ctaType: CtaType | null;
  translatePerkTitle: (perk: ServicePerk) => string;
}>) {
  if (isGallet || servicePerks.length === 0) return null;

  return (
    <div className={styles.perks}>
      {filterVisibleServicePerks(servicePerks, engravingEnabled, ctaType).map((perk) => {
        const translatedTitle = translatePerkTitle(perk);
        return (
          <button
            key={perk.id}
            type="button"
            className={styles.perk}
            onClick={() => {
              modalActions.open('service-perk', {
                heading: translatedTitle,
                modalText: perk.modalText,
                body: perk.body,
                linkUrl: perk.linkUrl,
              });
            }}
            aria-label={`${translatedTitle} information`}
          >
            <div className={styles.perkIconWrapper}>
              {BRAND === 'gallet' ? (
                <GalletPerkIcon icon={perk.icon} />
              ) : (
                <Image src={getPerkIconPath(perk.icon)} alt="" width={40} height={40} aria-hidden />
              )}
            </div>
            <span className={styles.perkTitle}>{translatedTitle}</span>
          </button>
        );
      })}
    </div>
  );
}

function ProductInfoVariantSelectors({
  isGallet,
  sku,
  images,
  bundleVariations,
  marketingPartnerSelector,
  router,
}: Readonly<{
  isGallet: boolean;
  sku: string;
  images?: Product['images'];
  bundleVariations?: Product['bundleVariations'];
  marketingPartnerSelector?: Product['marketingPartnerSelector'];
  router: { push: (href: string) => void };
}>) {
  const hasBundleChoices = Boolean(bundleVariations && bundleVariations.length > 1);
  const showBreitlingSelectors =
    (!isGallet && hasBundleChoices) || Boolean(marketingPartnerSelector);

  return (
    <>
      {/* Gallet: inline strap selector. Strip locale before push — i18n router
          re-adds it and would otherwise double the prefix. */}
      {isGallet && hasBundleChoices && bundleVariations ? (
        <StrapSelector
          currentSku={sku}
          variations={bundleVariations}
          onSelect={(selectedSku) => {
            navigateToSkuPath(router, selectedSku, { stripLocale: true });
          }}
        />
      ) : null}

      {showBreitlingSelectors ? (
        <div className={styles.selectorsRow}>
          {!isGallet && hasBundleChoices && bundleVariations ? (
            <ChangeStrapButton
              strapImage={bundleVariations[0].image || images?.[0]?.url || ''}
              strapName={bundleVariations[0].name || 'Current strap'}
              strapPreviewCoords={bundleVariations[0].strapPreviewCoords}
              onClick={() => {
                modalActions.open('change-strap', {
                  productSku: sku,
                  currentStrapImage: bundleVariations[0].image,
                  bundleVariations: bundleVariations.map((v) => ({
                    sku: v.sku,
                    name: v.name,
                    image: v.image,
                    color: v.color,
                    colorCode: v.colorCode,
                    material: v.material,
                    strapPreviewCoords: v.strapPreviewCoords,
                  })),
                  onSelect: (variation) => {
                    if (variation.sku) {
                      navigateToSkuPath(router, variation.sku);
                    }
                  },
                });
              }}
            />
          ) : null}

          {marketingPartnerSelector ? (
            <MarketingSelectorButton
              selectorData={marketingPartnerSelector}
              onClick={() => {
                modalActions.open('marketing-selector', {
                  title: marketingPartnerSelector.title,
                  marketingPartner: marketingPartnerSelector.marketingPartner,
                  items: marketingPartnerSelector.items.map((item) => ({
                    name: item.name,
                    reference: item.reference,
                    slug: item.slug,
                    sku: item.sku,
                    imageUrl: item.imageUrl,
                  })),
                  currentItemReference: marketingPartnerSelector.currentItem?.reference,
                  onSelect: (item) => {
                    if (item.sku) {
                      navigateToSkuPath(router, item.sku);
                    }
                  },
                });
              }}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
}

ProductInfo.displayName = 'ProductInfo';

function PrimaryPurchaseButton({
  buttonRef,
  isCatalogMode,
  isGallet,
  primaryCtaLabel,
  contactUsLabel,
  canAddToCart,
  isAdding,
  isCheckoutBusy,
  onPrimaryCta,
  onContactUs,
}: Readonly<{
  buttonRef?: Ref<HTMLButtonElement>;
  isCatalogMode: boolean;
  isGallet: boolean;
  primaryCtaLabel: string;
  contactUsLabel: string;
  canAddToCart: boolean;
  isAdding: boolean;
  isCheckoutBusy: boolean;
  onPrimaryCta: () => void;
  onContactUs: () => void;
}>) {
  if (isCatalogMode) {
    return (
      <Button
        ref={buttonRef}
        variant="primary"
        size="lg"
        fullWidth
        onClick={onContactUs}
        className={styles.addToBag}
        iconAfter={contactUsIcon}
      >
        {contactUsLabel}
      </Button>
    );
  }

  return (
    <Button
      ref={buttonRef}
      variant="primary"
      size="lg"
      fullWidth
      onClick={onPrimaryCta}
      disabled={isAdding || (canAddToCart && isCheckoutBusy)}
      loading={isAdding || (canAddToCart && isCheckoutBusy)}
      className={styles.addToBag}
      iconAfter={
        isGallet ? <Icon name="shopping-bag" size={16} className={styles.ctaIcon} /> : undefined
      }
    >
      {primaryCtaLabel}
    </Button>
  );
}

type ProductInfoActionLabels = {
  addEngraving: string;
  callToBuy: string;
  giveAsGift: string;
  gift: string;
  contactUs: string;
  availabilityRequest: string;
};

function ProductInfoActions({
  isCatalogMode,
  showAvailabilityRequest,
  isGallet,
  engravingEnabled,
  ctaType,
  showPrimaryCta,
  primaryCtaLabel,
  canAddToCart,
  isAdding,
  isCheckoutBusy,
  primaryCtaRef,
  showCallToBuy,
  isPreOrder,
  giftAvailable,
  sku,
  displayName,
  preOrderMessage,
  orLabel,
  labels,
  onEngraving,
  onPrimaryCta,
  onCallToBuy,
  onContactUs,
  onAvailabilityRequest,
  onGiveAsGift,
}: Readonly<{
  isCatalogMode: boolean;
  showAvailabilityRequest: boolean;
  isGallet: boolean;
  engravingEnabled: boolean;
  ctaType: CtaType | null;
  showPrimaryCta: boolean;
  primaryCtaLabel: string;
  canAddToCart: boolean;
  isAdding: boolean;
  isCheckoutBusy: boolean;
  primaryCtaRef?: Ref<HTMLButtonElement>;
  showCallToBuy: boolean;
  isPreOrder: boolean;
  giftAvailable: boolean;
  sku: string;
  displayName: string;
  preOrderMessage: string;
  orLabel: string;
  labels: ProductInfoActionLabels;
  onEngraving: () => void;
  onPrimaryCta: () => void;
  onCallToBuy: () => void;
  onContactUs: () => void;
  onAvailabilityRequest: () => void;
  onGiveAsGift?: () => void;
}>) {
  if (isCatalogMode) {
    if (showAvailabilityRequest) {
      return (
        <div className={styles.ctas}>
          <Button
            ref={primaryCtaRef}
            variant="outline"
            size="lg"
            fullWidth
            onClick={onAvailabilityRequest}
            className={styles.callToBuyCta}
          >
            {labels.availabilityRequest}
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.ctas}>
        <PrimaryPurchaseButton
          buttonRef={primaryCtaRef}
          isCatalogMode
          isGallet={isGallet}
          primaryCtaLabel={primaryCtaLabel}
          contactUsLabel={labels.contactUs}
          canAddToCart={canAddToCart}
          isAdding={isAdding}
          isCheckoutBusy={isCheckoutBusy}
          onPrimaryCta={onPrimaryCta}
          onContactUs={onContactUs}
        />
      </div>
    );
  }

  const handleGiveAsGift = () => {
    if (onGiveAsGift) {
      onGiveAsGift();
      return;
    }
    modalActions.open('send-gift', {
      sku,
      productName: displayName,
    });
  };

  return (
    <div className={styles.ctas}>
      {engravingEnabled && ctaType === 'ORDER' && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          onClick={onEngraving}
          className={clsx(styles.engravingCta, styles.engravingCtaSpacing)}
        >
          {labels.addEngraving}
        </Button>
      )}

      {showPrimaryCta && ctaType ? (
        <PrimaryPurchaseButton
          buttonRef={primaryCtaRef}
          isCatalogMode={false}
          isGallet={isGallet}
          primaryCtaLabel={primaryCtaLabel}
          contactUsLabel={labels.contactUs}
          canAddToCart={canAddToCart}
          isAdding={isAdding}
          isCheckoutBusy={isCheckoutBusy}
          onPrimaryCta={onPrimaryCta}
          onContactUs={onContactUs}
        />
      ) : null}

      {isGallet && showCallToBuy && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          onClick={onCallToBuy}
          className={styles.callToBuyCta}
          iconAfter={<Icon name="phone" size={16} className={styles.ctaIcon} />}
        >
          {labels.callToBuy}
        </Button>
      )}

      {isPreOrder && <p className={styles.preOrderInfo}>{preOrderMessage}</p>}

      {giftAvailable && ctaType === 'ORDER' && (
        <>
          <div className={styles.orDivider}>
            <span className={styles.orLine} />
            <span className={styles.orText}>{orLabel}</span>
            <span className={styles.orLine} />
          </div>

          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={handleGiveAsGift}
            className={styles.giveAsGift}
          >
            <span className={styles.giftButtonContent}>
              {labels.giveAsGift}
              <Image
                src="/images/breitling/icons/gift-icon.svg"
                alt=""
                width={20}
                height={20}
                className={styles.giftIcon}
                aria-hidden
              />
              {labels.gift}
            </span>
          </Button>
        </>
      )}
    </div>
  );
}

function ProductInfoSecondaryLinks({
  isGallet,
  isCatalogMode,
  showCallToBuy,
  showNotifyMe,
  callToBuyLabel,
  bookAppointmentLabel,
  notifyMeLabel,
  onCallToBuy,
  onBookAppointment,
  onNotifyMe,
}: Readonly<{
  isGallet: boolean;
  isCatalogMode: boolean;
  showCallToBuy: boolean;
  showNotifyMe: boolean;
  callToBuyLabel: string;
  bookAppointmentLabel: string;
  notifyMeLabel: string;
  onCallToBuy: () => void;
  onBookAppointment: () => void;
  onNotifyMe: () => void;
}>) {
  // Breitling only: Gallet renders Notify Me next to Book an Appointment above the perks.
  if (isGallet) {
    return null;
  }

  return (
    <div className={styles.secondaryLinks}>
      {!isGallet && showCallToBuy && (
        <button type="button" className={styles.secondaryLink} onClick={onCallToBuy}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            className={styles.linkIcon}
          >
            <path
              d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{callToBuyLabel}</span>
        </button>
      )}
      {!isGallet && (
        <BookAppointmentButton
          className={styles.secondaryLink}
          label={bookAppointmentLabel}
          onClick={onBookAppointment}
        />
      )}
      {showNotifyMe && !isCatalogMode && (
        <NotifyMeButton
          className={styles.secondaryLink}
          label={notifyMeLabel}
          onClick={onNotifyMe}
        />
      )}
    </div>
  );
}

/** Check-mark bullet used by every Gallet service-perk row. */
function GalletPerkCheckIcon() {
  return (
    <svg
      className={styles.galletPerkCheck}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M3 8.5l3.5 3.5L13 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Book-an-Appointment link: calendar icon + underlined label. Shared so it can
 * render in the Gallet position (below the CTAs, above the perks) and the
 * Breitling position (secondary links) from a single definition.
 */
/** Envelope + label; Gallet styles it like Book an Appointment, Breitling as a secondary link. */
function NotifyMeButton({
  label,
  onClick,
  className,
}: Readonly<{
  label: string;
  onClick: () => void;
  className?: string;
}>) {
  return (
    <button type="button" className={className} onClick={onClick}>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className={styles.linkIcon}
      >
        <path
          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M22 6l-10 7L2 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>{label}</span>
    </button>
  );
}

function BookAppointmentButton({
  label,
  onClick,
  className,
}: Readonly<{
  label: string;
  onClick: () => void;
  className?: string;
}>) {
  return (
    <button type="button" className={className} onClick={onClick}>
      <CalendarIcon className={styles.linkIcon} />
      <span>{label}</span>
    </button>
  );
}

/** Calendar icon — `currentColor` so it inherits brand primary from the link. */
function CalendarIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3.59097 18.3333C2.69472 18.3333 2.01946 18.1062 1.56519 17.6519C1.11092 17.2038 0.883789 16.5346 0.883789 15.6445V4.35539C0.883789 3.46528 1.11092 2.79615 1.56519 2.34803C2.01946 1.89376 2.69472 1.66663 3.59097 1.66663H16.4087C17.311 1.66663 17.9863 1.89376 18.4344 2.34803C18.8887 2.79615 19.1158 3.46528 19.1158 4.35539V15.6445C19.1158 16.5285 18.8887 17.1976 18.4344 17.6519C17.9863 18.1062 17.311 18.3333 16.4087 18.3333H3.59097ZM3.51731 17.2007H16.4823C16.9611 17.2007 17.3295 17.0718 17.5873 16.814C17.8513 16.5623 17.9832 16.1878 17.9832 15.6906V7.26515C17.9832 6.76792 17.8513 6.39345 17.5873 6.14176C17.3295 5.88394 16.9611 5.75502 16.4823 5.75502H3.51731C3.03235 5.75502 2.66095 5.88394 2.40313 6.14176C2.1453 6.39345 2.01639 6.76792 2.01639 7.26515V15.6906C2.01639 16.1878 2.1453 16.5623 2.40313 16.814C2.66095 17.0718 3.03235 17.2007 3.51731 17.2007ZM8.17661 9.03311C8.03542 9.03311 7.9372 9.00855 7.88195 8.95944C7.83284 8.91033 7.80828 8.81518 7.80828 8.67399V8.13071C7.80828 7.98952 7.83284 7.89744 7.88195 7.85447C7.9372 7.80536 8.03542 7.78081 8.17661 7.78081H8.71988C8.86108 7.78081 8.95623 7.80536 9.00534 7.85447C9.06059 7.89744 9.08821 7.98952 9.08821 8.13071V8.67399C9.08821 8.81518 9.06059 8.91033 9.00534 8.95944C8.95623 9.00855 8.86108 9.03311 8.71988 9.03311H8.17661ZM11.2982 9.03311C11.157 9.03311 11.0618 9.00855 11.0127 8.95944C10.9636 8.91033 10.939 8.81518 10.939 8.67399V8.13071C10.939 7.98952 10.9636 7.89744 11.0127 7.85447C11.0618 7.80536 11.157 7.78081 11.2982 7.78081H11.8414C11.9888 7.78081 12.087 7.80536 12.1361 7.85447C12.1852 7.89744 12.2098 7.98952 12.2098 8.13071V8.67399C12.2098 8.81518 12.1852 8.91033 12.1361 8.95944C12.087 9.00855 11.9888 9.03311 11.8414 9.03311H11.2982ZM14.4289 9.03311C14.2816 9.03311 14.1834 9.00855 14.1342 8.95944C14.0851 8.91033 14.0606 8.81518 14.0606 8.67399V8.13071C14.0606 7.98952 14.0851 7.89744 14.1342 7.85447C14.1834 7.80536 14.2816 7.78081 14.4289 7.78081H14.963C15.1103 7.78081 15.2085 7.80536 15.2576 7.85447C15.3067 7.89744 15.3313 7.98952 15.3313 8.13071V8.67399C15.3313 8.81518 15.3067 8.91033 15.2576 8.95944C15.2085 9.00855 15.1103 9.03311 14.963 9.03311H14.4289ZM5.05506 12.1086C4.90773 12.1086 4.80951 12.0841 4.7604 12.035C4.71129 11.9858 4.68674 11.8907 4.68674 11.7495V11.2154C4.68674 11.0681 4.71129 10.9729 4.7604 10.93C4.80951 10.8809 4.90773 10.8563 5.05506 10.8563H5.59834C5.73953 10.8563 5.83468 10.8809 5.88379 10.93C5.9329 10.9729 5.95745 11.0681 5.95745 11.2154V11.7495C5.95745 11.8907 5.9329 11.9858 5.88379 12.035C5.83468 12.0841 5.73953 12.1086 5.59834 12.1086H5.05506ZM8.17661 12.1086C8.03542 12.1086 7.9372 12.0841 7.88195 12.035C7.83284 11.9858 7.80828 11.8907 7.80828 11.7495V11.2154C7.80828 11.0681 7.83284 10.9729 7.88195 10.93C7.9372 10.8809 8.03542 10.8563 8.17661 10.8563H8.71988C8.86108 10.8563 8.95623 10.8809 9.00534 10.93C9.06059 10.9729 9.08821 11.0681 9.08821 11.2154V11.7495C9.08821 11.8907 9.06059 11.9858 9.00534 12.035C8.95623 12.0841 8.86108 12.1086 8.71988 12.1086H8.17661ZM11.2982 12.1086C11.157 12.1086 11.0618 12.0841 11.0127 12.035C10.9636 11.9858 10.939 11.8907 10.939 11.7495V11.2154C10.939 11.0681 10.9636 10.9729 11.0127 10.93C11.0618 10.8809 11.157 10.8563 11.2982 10.8563H11.8414C11.9888 10.8563 12.087 10.8809 12.1361 10.93C12.1852 10.9729 12.2098 11.0681 12.2098 11.2154V11.7495C12.2098 11.8907 12.1852 11.9858 12.1361 12.035C12.087 12.0841 11.9888 12.1086 11.8414 12.1086H11.2982ZM14.4289 12.1086C14.2816 12.1086 14.1834 12.0841 14.1342 12.035C14.0851 11.9858 14.0606 11.8907 14.0606 11.7495V11.2154C14.0606 11.0681 14.0851 10.9729 14.1342 10.93C14.1834 10.8809 14.2816 10.8563 14.4289 10.8563H14.963C15.1103 10.8563 15.2085 10.8809 15.2576 10.93C15.3067 10.9729 15.3313 11.0681 15.3313 11.2154V11.7495C15.3313 11.8907 15.3067 11.9858 15.2576 12.035C15.2085 12.0841 15.1103 12.1086 14.963 12.1086H14.4289ZM5.05506 15.1841C4.90773 15.1841 4.80951 15.1626 4.7604 15.1197C4.71129 15.0706 4.68674 14.9723 4.68674 14.825V14.2909C4.68674 14.1436 4.71129 14.0485 4.7604 14.0055C4.80951 13.9564 4.90773 13.9318 5.05506 13.9318H5.59834C5.73953 13.9318 5.83468 13.9564 5.88379 14.0055C5.9329 14.0485 5.95745 14.1436 5.95745 14.2909V14.825C5.95745 14.9723 5.9329 15.0706 5.88379 15.1197C5.83468 15.1626 5.73953 15.1841 5.59834 15.1841H5.05506ZM8.17661 15.1841C8.03542 15.1841 7.9372 15.1626 7.88195 15.1197C7.83284 15.0706 7.80828 14.9723 7.80828 14.825V14.2909C7.80828 14.1436 7.83284 14.0485 7.88195 14.0055C7.9372 13.9564 8.03542 13.9318 8.17661 13.9318H8.71988C8.86108 13.9318 8.95623 13.9564 9.00534 14.0055C9.06059 14.0485 9.08821 14.1436 9.08821 14.2909V14.825C9.08821 14.9723 9.06059 15.0706 9.00534 15.1197C8.95623 15.1626 8.86108 15.1841 8.71988 15.1841H8.17661ZM11.2982 15.1841C11.157 15.1841 11.0618 15.1626 11.0127 15.1197C10.9636 15.0706 10.939 14.9723 10.939 14.825V14.2909C10.939 14.1436 10.9636 14.0485 11.0127 14.0055C11.0618 13.9564 11.157 13.9318 11.2982 13.9318H11.8414C11.9888 13.9318 12.087 13.9564 12.1361 14.0055C12.1852 14.0485 12.2098 14.1436 12.2098 14.2909V14.825C12.2098 14.9723 12.1852 15.0706 12.1361 15.1197C12.087 15.1626 11.9888 15.1841 11.8414 15.1841H11.2982Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Original info-badge shape from /images/breitling/info-badge.svg, re-rendered
// inline with fill="currentColor" so callers can theme it via CSS color.
function InfoBadgeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21508 0.913451 7.4078C0.00519941 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.807 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0865C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6934 24 14.3734 24 12C24 10.4241 23.6896 8.8637 23.0866 7.4078C22.4835 5.95189 21.5996 4.62902 20.4853 3.51472C19.371 2.40042 18.0481 1.5165 16.5922 0.913445C15.1363 0.310389 13.5759 0 12 0ZM13.2 18H10.8V10.8H13.2V18ZM13.2 8.4H10.8V6H13.2V8.4Z"
        fill="currentColor"
      />
    </svg>
  );
}
